import type { Track } from '@/world/Track';
import type { City, SceneryKind } from '@/config/cities';
import { Rng } from '@/core/Rng';
import { zoneKind } from './place';
import { FOLIAGE, FOLIAGE_AUTUMN, LENGA, RIO_JUNGLE, SAKURA } from './palettes';

/**
 * scenery/leanTrees.ts — LEANING OVERHANG TREES (M98, pure placement).
 *
 * The owner's spec: the environment should still "bleed onto the track", but
 * as trees LEANING over the road with their leaves hanging above it — never as
 * the bulging hill domes that used to wall off the view of the ball. So this
 * module plants deliberate lean-in trees along eligible stretches: trunk on
 * the verge, canopy reaching up to ~8 units over the road with its underside
 * kept HIGH (≥ ~6 — the chase camera rides at ~4.5, the ball at 1.6, so
 * nothing on the sight line is ever covered; enforced by a cross-city test).
 *
 * Placement is a pure function of the city (own rng stream — the main scenery
 * sequence is untouched), and it stays clear of everything that owns its own
 * moment: the start grid, the finish plaza + clearing window, every landmark,
 * the launch ramp's flight path and the interactive features.
 */

export type LeanSpecies = 'park' | 'jungle' | 'sakura' | 'umbrella' | 'dry' | 'lenga' | 'palm';

export interface LeanTree {
  /** Trunk base (world, on the verge). */
  x: number;
  y: number;
  z: number;
  /** Track direction at the base (§6 heading). */
  dir: number;
  /** Which side of the road the trunk stands on (-1 left / +1 right). */
  side: -1 | 1;
  /** Lean angle toward the road (radians from vertical). */
  lean: number;
  /** Overall scale. */
  s: number;
  species: LeanSpecies;
  /** Canopy colour (species palette). */
  color: number;
  /** Deterministic per-tree jitter seed for the mesh builder. */
  seed: number;
}

/** Species shape presets — trunk length + canopy blob layout, all × the
 *  instance scale `s`. `reach` runs INWARD (over the road), `rise` up from the
 *  trunk tip; sy squashes a blob vertically (umbrella crowns). */
export const LEAN_SHAPES: Record<
  Exclude<LeanSpecies, 'palm'>,
  {
    trunkLen: number;
    lean: [number, number];
    trunkColor: number;
    blobs: { dx: number; dy: number; r: number; sy: number }[];
  }
> = {
  park: {
    trunkLen: 8.8,
    lean: [0.3, 0.44],
    trunkColor: 0x5a3f2a,
    blobs: [
      { dx: 0.6, dy: 0.5, r: 2.5, sy: 0.82 },
      { dx: 2.4, dy: -0.1, r: 1.9, sy: 0.78 },
      { dx: -0.9, dy: -0.2, r: 1.6, sy: 0.8 },
    ],
  },
  jungle: {
    trunkLen: 9.6,
    lean: [0.34, 0.5],
    trunkColor: 0x4c3826,
    blobs: [
      { dx: 0.7, dy: 0.5, r: 2.7, sy: 0.8 },
      { dx: 2.9, dy: -0.2, r: 2.1, sy: 0.72 },
      { dx: -1.0, dy: 0.1, r: 1.8, sy: 0.8 },
      { dx: 1.8, dy: -0.75, r: 1.2, sy: 0.85 }, // the low "hanging leaves" tuft
    ],
  },
  sakura: {
    trunkLen: 7.9,
    lean: [0.3, 0.42],
    trunkColor: 0x3f3230,
    blobs: [
      { dx: 0.5, dy: 0.4, r: 2.2, sy: 0.8 },
      { dx: 2.0, dy: -0.1, r: 1.7, sy: 0.75 },
      { dx: -0.8, dy: -0.1, r: 1.4, sy: 0.8 },
      { dx: 1.3, dy: -0.7, r: 1.0, sy: 0.85 },
    ],
  },
  umbrella: {
    trunkLen: 10.2,
    lean: [0.24, 0.36],
    trunkColor: 0x6d4f30,
    blobs: [
      { dx: 0.8, dy: 0.5, r: 3.1, sy: 0.5 }, // one wide flat parasol
      { dx: 2.6, dy: 0.1, r: 2.0, sy: 0.5 },
    ],
  },
  dry: {
    trunkLen: 8.0,
    lean: [0.3, 0.42],
    trunkColor: 0x6a5638,
    blobs: [
      { dx: 0.7, dy: 0.4, r: 2.1, sy: 0.62 },
      { dx: 2.2, dy: 0.0, r: 1.5, sy: 0.6 },
    ],
  },
  lenga: {
    trunkLen: 8.9,
    lean: [0.4, 0.55], // wind-flagged hard toward the road
    trunkColor: 0x4a3a2c,
    blobs: [
      { dx: 1.0, dy: 0.4, r: 2.3, sy: 0.75 },
      { dx: 3.0, dy: -0.15, r: 1.8, sy: 0.7 }, // the flag: canopy blown inward
      { dx: -0.5, dy: -0.1, r: 1.3, sy: 0.75 },
    ],
  },
};

/** Palm crown numbers (mirrors the standing buildPalms proportions). */
export const LEAN_PALM = { segLen: 3.3, lean: [0.26, 0.4] as [number, number], trunkColor: 0xa8845a };

// ---- The lean-palm CROWN (final-polish round) -----------------------------
//
// THE DEFECT: from the road and from above the lean palms read as flat green
// PAPER STARS (QA, lagos re-review #14). The shared blade geometry was already
// rebuilt (builders' PALM_FROND: tapered, arched, midrib-creased) and the fan
// already uses a dedicated 'YXZ' euler, so the leaf itself is fine — what reads
// as paper is the POSE: all seven blades sat at the SAME length, at the SAME
// shallow pitch band (0.18-0.30 rad ≈ 10-17° — near horizontal), spaced at
// EXACTLY 2π/7. Perfect radial symmetry + one pitch + one length is the
// definition of a star cut from paper; no leaf shape survives it.
//
// THE MODEL: a real crown has age bands — a near-upright young spear, mature
// fronds arching out, and older ones drooping hard and hanging shorter — at
// irregular azimuths. That is what this reproduces, all of it from INDEX
// HASHES of the tree's own seed (never an rng draw, so nothing moves).
//
// THE ENVELOPE IS THE CONTRACT. The chase camera rides ~4.5 up and the ball
// sight line must stay clear, so `canopyClearance` (below) promises the crown
// never dips more than PALM_DIP_MODEL (1.35) × s under the trunk tip, and
// placeLeanTrees flattens a tree's lean until that promise holds. This model
// therefore stays inside the CURRENT RENDERED envelope on both axes — proven
// per placed palm, in every city, by tests/world/leanPalmCrown.test.ts:
//   reach ≤ 3.865·s (today's 1.7 + 2.2·cos 0.18)   → new max 3.55·s
//   dip   ≤ 1.246·s (today's 0.1 + 0.52c + 2.2s at 0.30) → new max 1.23·s
// Deep droop costs dip budget FAST (2.2·sin dominates), which is exactly why
// the drooping band is also the SHORTEST band here — botanically right and the
// only way to buy droop without lowering the canopy.

/** Geometry facts of the shared blade (builders' PALM_FROND) this model poses.
 *  The blade spans z −2.2..+2.2 about its pivot, its arched tip hangs 0.52
 *  under the blade axis, and the fan seats 0.1 under the trunk tip. Keep these
 *  in step with palmFrondGeo() or the clearance promise stops being true. */
export const LEAN_FROND = { half: 2.2, tipSag: 0.52, seat: 0.1 };

/** Fronds per crown — the InstancedMesh allocation in builders.ts (n × 7). */
export const LEAN_PALM_FRONDS = 7;

/** Conservative dip the clearance model assumes (× s). The real crown must
 *  stay under it; a test pins that so the model can never go optimistic. */
export const PALM_DIP_MODEL = 1.35;

/** One posed blade of a palm crown. All lengths are × the tree's scale `s`;
 *  `yaw` is a world heading, `pitch` droops the tip DOWN (the 'YXZ' euler's
 *  X term), `radial` seats the pivot out from the crown centre. */
export interface LeanFrondPose {
  radial: number;
  yaw: number;
  pitch: number;
  len: number;
}

/** Mirrors builders' `hash()` so a crown poses identically on both sides of
 *  the seam (variety WITHOUT touching any rng stream — the M36 rule). */
function fhash(n: number): number {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/** The three age bands: [pitch lo, pitch hi, len lo, len hi, radial lo, radial hi].
 *  young = a near-upright spear (its tip sits ABOVE the seat, so it costs no
 *  dip budget and gives the crown real vertical extent); mature = the arching
 *  body of the crown; old = hard droop, deliberately SHORT (see the note). */
const FROND_BANDS: readonly [number, number, number, number, number, number][] = [
  [-0.24, -0.06, 0.66, 0.84, 0.92, 1.24], // young
  [0.11, 0.26, 0.93, 1.05, 1.32, 1.48], // mature
  [0.33, 0.46, 0.68, 0.78, 1.36, 1.52], // old
];
/** Which band each slot wears; rotated per palm so no two crowns match. */
const BAND_ORDER = [0, 1, 2, 1, 2, 1, 0] as const;

/** Pose a palm crown's seven blades. Pure, deterministic in the tree's seed. */
export function leanPalmFronds(seed: number): LeanFrondPose[] {
  const roll = Math.floor(fhash(seed * 5 + 1) * LEAN_PALM_FRONDS) % LEAN_PALM_FRONDS;
  const base = fhash(seed) * Math.PI * 2;
  const out: LeanFrondPose[] = [];
  for (let f = 0; f < LEAN_PALM_FRONDS; f++) {
    const [p0, p1, l0, l1, r0, r1] = FROND_BANDS[BAND_ORDER[(f + roll) % LEAN_PALM_FRONDS]];
    const hp = fhash(seed + f * 7);
    const hl = fhash(seed * 3 + f * 11);
    const hr = fhash(seed * 7 + f * 5);
    out.push({
      // ±0.17 rad of azimuth jitter — the even 2π/7 spacing WAS the star.
      yaw: base + (f / LEAN_PALM_FRONDS) * Math.PI * 2 + (fhash(seed + f * 13) - 0.5) * 0.34,
      pitch: p0 + hp * (p1 - p0),
      len: l0 + hl * (l1 - l0),
      radial: r0 + hr * (r1 - r0),
    });
  }
  return out;
}

/** How far a posed crown reaches from its centre (× s) — the blade tip's
 *  radial distance, the value `canopyReach` needs to be honest. */
export function leanPalmCrownReach(seed: number): number {
  let r = 0;
  for (const f of leanPalmFronds(seed)) {
    r = Math.max(r, f.radial + LEAN_FROND.half * f.len * Math.cos(f.pitch));
  }
  return r;
}

/** How far a posed crown hangs BELOW the trunk tip (× s). The tip is the
 *  lowest point of a blade (the arch falls monotonically past its shoulder and
 *  the pitch only steepens that), so the tips are the whole story. */
export function leanPalmCrownDip(seed: number): number {
  let d = 0;
  for (const f of leanPalmFronds(seed)) {
    const tip = f.len * (LEAN_FROND.tipSag * Math.cos(f.pitch) + LEAN_FROND.half * Math.sin(f.pitch));
    d = Math.max(d, LEAN_FROND.seat + tip);
  }
  return d;
}

interface ZoneLean {
  species: LeanSpecies;
  colors: readonly number[];
  /** Sides trees may stand on (water sides excluded per the city survey). */
  sides: readonly (-1 | 1)[];
  /** Chance per placement step (≈ every 9 units). */
  chance: number;
}

const DRY_OLIVE = [0x8a8a4a, 0x9a9456, 0x7a7a40] as const;
const BOTH = [-1, 1] as const;
const LEFT = [-1] as const;

/** Which stretches of each city grow lean-in trees. Water sides are excluded
 *  (the Thames, Tokyo Bay, the lagoon causeway, Lac Rose, the Arabian Sea,
 *  Patagonia's lakes are all side +1 in their zones). */
export const LEAN_CONFIG: Record<string, Partial<Record<SceneryKind, ZoneLean>>> = {
  nyc: {
    brownstones: { species: 'park', colors: FOLIAGE, sides: BOTH, chance: 0.1 },
    park: { species: 'park', colors: [...FOLIAGE, ...FOLIAGE_AUTUMN], sides: BOTH, chance: 0.2 },
  },
  london: {
    westminster: { species: 'park', colors: FOLIAGE, sides: BOTH, chance: 0.1 },
    thames: { species: 'park', colors: FOLIAGE, sides: LEFT, chance: 0.16 },
  },
  napoli: {
    bayfront: { species: 'umbrella', colors: FOLIAGE, sides: LEFT, chance: 0.15 },
    castello: { species: 'umbrella', colors: FOLIAGE, sides: BOTH, chance: 0.09 },
  },
  tokyo: {
    asakusa: { species: 'sakura', colors: SAKURA, sides: BOTH, chance: 0.2 },
  },
  rio: {
    copacabana: { species: 'palm', colors: [0x3aa050], sides: BOTH, chance: 0.15 },
    tijuca: { species: 'jungle', colors: RIO_JUNGLE, sides: BOTH, chance: 0.26 },
  },
  dakar: {
    savanna: { species: 'dry', colors: DRY_OLIVE, sides: BOTH, chance: 0.16 },
    lacrose: { species: 'dry', colors: DRY_OLIVE, sides: LEFT, chance: 0.1 },
  },
  lagos: {
    balogun: { species: 'palm', colors: [0x3aa050], sides: BOTH, chance: 0.09 },
    victoriaIsland: { species: 'palm', colors: [0x3aa050], sides: BOTH, chance: 0.17 },
    lekki: { species: 'palm', colors: [0x3aa050], sides: LEFT, chance: 0.22 },
  },
  mumbai: {
    fort: { species: 'park', colors: FOLIAGE, sides: BOTH, chance: 0.08 },
    marinedrive: { species: 'palm', colors: [0x3aa050], sides: LEFT, chance: 0.16 },
    hajiali: { species: 'palm', colors: [0x3aa050], sides: LEFT, chance: 0.1 },
  },
  patagonia: {
    lenga: { species: 'lenga', colors: LENGA, sides: BOTH, chance: 0.22 },
    glaciallake: { species: 'lenga', colors: LENGA, sides: LEFT, chance: 0.1 },
  },
};

/** Layout constants (knobs). */
export const LEAN = {
  step: 9, // placement stride along the course
  startClear: 80, // nothing over the grid / opening straight
  finishClear: 85, // the plaza + clearing window own the last stretch
  landmarkClear: 42, // no tree beside/inside a landmark's moment
  rampClearAhead: 34, // …nor over the ramp jump's flight path
  featureClear: 14,
  minGap: 24, // same-side spacing
  baseLat: 2.3, // trunk base beyond the rail…
  baseLatJitter: 1.4, // …plus up to this
  minCanopyClearance: 6.0, // canopy underside stays above this (ball never hidden)
};

/** Course windows no tree may occupy (pure; from the city definition). */
export function leanBlockers(city: City, finish: number): [number, number][] {
  const out: [number, number][] = [
    [-1e9, LEAN.startClear],
    [finish - LEAN.finishClear, 1e9],
  ];
  for (const lm of city.landmarks ?? []) {
    out.push([lm.at * finish - LEAN.landmarkClear, lm.at * finish + LEAN.landmarkClear]);
  }
  if (city.ramp) {
    const r0 = city.ramp.at * finish;
    out.push([r0 - 10, r0 + city.ramp.length + LEAN.rampClearAhead]);
  }
  for (const f of city.features ?? []) {
    out.push([f.at * finish - LEAN.featureClear, f.at * finish + LEAN.featureClear]);
  }
  return out;
}

/** The canopy's lowest point above the trunk-base ground (shared with the mesh
 *  builder so the safety test measures exactly what renders). */
export function canopyClearance(t: LeanTree): number {
  if (t.species === 'palm') {
    // Crown centre at the curved trunk's tip; the fan pivots there and hangs
    // at most PALM_DIP_MODEL·s below it. This stays a CONSTANT on purpose: it
    // is the promise placeLeanTrees flattens a lean against, so the posed
    // crown must fit under it rather than the other way round (a
    // seed-dependent clearance would move trees). leanPalmCrownDip() measures
    // what the crown actually does; a test pins dip ≤ PALM_DIP_MODEL.
    const tip = tipOf(t);
    return tip.rise - PALM_DIP_MODEL * t.s;
  }
  const shape = LEAN_SHAPES[t.species];
  const tipRise = Math.cos(t.lean) * shape.trunkLen * t.s;
  let low = Infinity;
  for (const b of shape.blobs) {
    low = Math.min(low, tipRise + (b.dy - b.r * b.sy) * t.s);
  }
  return low;
}

/** Trunk tip: how high it rises and how far it reaches over the road. */
export function tipOf(t: LeanTree): { rise: number; reach: number } {
  if (t.species === 'palm') {
    // Three stacked segments with a progressive bend (0.55/1.0/1.45 × lean).
    let rise = 0;
    let reach = 0;
    for (const k of [0.55, 1.0, 1.45]) {
      rise += Math.cos(t.lean * k) * LEAN_PALM.segLen * t.s;
      reach += Math.sin(t.lean * k) * LEAN_PALM.segLen * t.s;
    }
    return { rise, reach };
  }
  const shape = LEAN_SHAPES[t.species];
  return {
    rise: Math.cos(t.lean) * shape.trunkLen * t.s,
    reach: Math.sin(t.lean) * shape.trunkLen * t.s,
  };
}

/** How far the canopy's inner edge reaches past the trunk base, toward/over
 *  the road (for the audit/tests). */
export function canopyReach(t: LeanTree): number {
  const tip = tipOf(t);
  // MEASURED CORRECTION (final-polish): this used to read `+ 2.2·s`, the raw
  // blade half-length — but the fan's pivots are seated OUT from the crown
  // centre, so the real outer radius was ~3.87·s and the corridor invariant
  // was policing a number ~1.7·s smaller than what renders. It now asks the
  // pose model, so mesh and audit can never disagree again.
  if (t.species === 'palm') return tip.reach + leanPalmCrownReach(t.seed) * t.s;
  const shape = LEAN_SHAPES[t.species];
  let reach = tip.reach;
  for (const b of shape.blobs) reach = Math.max(reach, tip.reach + (b.dx + b.r) * t.s);
  return reach;
}

/** Place every leaning tree for a city. Deterministic (own seeded stream). */
export function placeLeanTrees(track: Track, city: City): LeanTree[] {
  const cfg = LEAN_CONFIG[city.id];
  if (!cfg) return [];
  const rng = new Rng((city.seed ^ 0x1ea717) >>> 0);
  const hw = track.halfWidth;
  const finish = track.finishDistance;
  const route = city.route ?? [{ until: 1, kind: 'generic' as SceneryKind }];
  const blockers = leanBlockers(city, finish);
  const lastAt: Record<number, number> = { [-1]: -1e9, [1]: -1e9 };
  const out: LeanTree[] = [];

  for (let dist = LEAN.startClear; dist < finish - LEAN.finishClear; dist += LEAN.step) {
    const zone = cfg[zoneKind(route, dist / finish)];
    if (!zone) continue;
    // One fixed draw per eligible step keeps the stream stable and simple.
    if (rng.next() >= zone.chance) continue;
    if (blockers.some(([a, b]) => dist > a && dist < b)) continue;
    const side = zone.sides[zone.sides.length === 1 ? 0 : rng.int(0, 1)];
    if (dist - lastAt[side] < LEAN.minGap) continue;

    const s = rng.range(0.88, 1.22);
    const leanRange = zone.species === 'palm' ? LEAN_PALM.lean : LEAN_SHAPES[zone.species].lean;
    const t: LeanTree = {
      x: 0,
      y: 0,
      z: 0,
      dir: 0,
      side,
      lean: rng.range(leanRange[0], leanRange[1]),
      s,
      species: zone.species,
      color: rng.pick(zone.colors as number[]),
      seed: rng.int(0, 1 << 30),
    };
    const lat = hw + LEAN.baseLat + rng.range(0, LEAN.baseLatJitter);
    const smp = track.sampleAt(dist);
    t.x = smp.x + smp.rightX * side * lat;
    t.y = smp.y;
    t.z = smp.z + smp.rightZ * side * lat;
    t.dir = smp.dir;
    // Safety: a roll that would dip the canopy under the clearance flattens
    // its lean until it clears (never silently drops the tree).
    while (canopyClearance(t) < LEAN.minCanopyClearance && t.lean > 0.12) t.lean -= 0.03;
    if (canopyClearance(t) < LEAN.minCanopyClearance) continue;
    lastAt[side] = dist;
    out.push(t);
  }
  return out;
}
